import { PixelCanvas } from '../game/pixel.js';
import { smoothToCanvas } from '../game/smooth.js';

/**
 * 12x12 pixel icons, drawn as text so they are easy to tweak. Each letter is
 * a palette entry; '.' is transparent.
 */

const PALETTE: Record<string, string> = {
  k: '#2a1e19',
  w: '#fffaf0',
  c: '#f3e2b3',
  y: '#f0bf36',
  b: '#b17a48',
  d: '#6b4226',
  r: '#d9433b',
  g: '#3d9e8c',
  n: '#3a4cb0',
  s: '#a9b0b7',
  p: '#f19bb7',
  l: '#7fb7d9',
};

const ICONS = {
  rooms: [
    '....kkkk....',
    '...kyyyyk...',
    '..kyyyyyyk..',
    '.kkkkkkkkkk.',
    '.kcccccccck.',
    '.kcllkklllk.',
    '.kcllkklllk.',
    '.kcccccccck.',
    '.kcckddkcck.',
    '.kcckddkcck.',
    '.kcckdykcck.',
    '.kkkkkkkkkk.',
  ],
  shop: [
    '....kkkk....',
    '...k....k...',
    '...k....k...',
    '.kkkkkkkkkk.',
    '.krrrrrrrrk.',
    '.krrrrrrrrk.',
    '.krrwwwwrrk.',
    '.krrrrrrrrk.',
    '.krrrrrrrrk.',
    '.krrrrrrrrk.',
    '.kddddddddk.',
    '.kkkkkkkkkk.',
  ],
  inventory: [
    '............',
    '..kkkkkkkk..',
    '.kbbbbbbbbk.',
    'kkkkkkkkkkkk',
    'kbbbbyybbbbk',
    'kbbbbyybbbbk',
    'kddddddddddk',
    'kbbbbbbbbbbk',
    'kbbbbbbbbbbk',
    'kbbbbbbbbbbk',
    'kkkkkkkkkkkk',
    '............',
  ],
  wardrobe: [
    '............',
    '..kkk..kkk..',
    '.kgggkkgggk.',
    'kggggggggggk',
    'kggkggggkggk',
    'kkkkggggkkkk',
    '...kggggk...',
    '...kggggk...',
    '...kggggk...',
    '...kggggk...',
    '...kkkkkk...',
    '............',
  ],
  trophy: [
    '.kkkkkkkkkk.',
    'kkyyyyyyyykk',
    'kykyyyyyykyk',
    'kykyyyyyykyk',
    '.kkyyyyyykk.',
    '...kyyyyk...',
    '....kyyk....',
    '.....kk.....',
    '....kyyk....',
    '...kyyyyk...',
    '..kkkkkkkk..',
    '..kddddddk..',
  ],
  help: [
    '...kkkkkk...',
    '..kyyyyyyk..',
    '.kyykkkkyyk.',
    '.kyk....kyk.',
    '.kkk...kyyk.',
    '......kyyk..',
    '.....kyyk...',
    '.....kyk....',
    '.....kkk....',
    '............',
    '.....kkk....',
    '.....kyk....',
  ],
  coin: [
    '...kkkkkk...',
    '..kyyyyyyk..',
    '.kyywwyyyyk.',
    'kyywykkkyydk',
    'kywyykyyyydk',
    'kyyyykkkyydk',
    'kyyyyyyykydk',
    'kyyyykkkyydk',
    'kyyyyykyyydk',
    '.kyyyyyyydk.',
    '..kddddddk..',
    '...kkkkkk...',
  ],
  flag: [
    '.kk.........',
    '.kkkkkkkkk..',
    '.kkrrrrrrrk.',
    '.kkrwrrrrrk.',
    '.kkrrrrrrrk.',
    '.kkkkkkkkk..',
    '.kk.........',
    '.kk.........',
    '.kk.........',
    '.kk.........',
    'kkkk........',
    '............',
  ],
  more: [
    '............',
    '............',
    '............',
    '............',
    '............',
    '.kk..kk..kk.',
    '.kk..kk..kk.',
    '............',
    '............',
    '............',
    '............',
    '............',
  ],
  heart: [
    '............',
    '.kkk...kkk..',
    'krrrk.krrrk.',
    'krwrrkrrrrk.',
    'krrrrrrrrrk.',
    'krrrrrrrrrk.',
    '.krrrrrrrk..',
    '..krrrrrk...',
    '...krrrk....',
    '....krk.....',
    '.....k......',
    '............',
  ],
  wave: [
    '.....kk.....',
    '..kk.kck....',
    '.kck.kck.kk.',
    '.kck.kck.kck',
    '.kckkkckkkck',
    '.kcckcckcck.',
    'kkcccccccck.',
    'kcccccccck..',
    '.kccccccck..',
    '..kcccccck..',
    '...kccccck..',
    '....kkkkk...',
  ],
  dance: [
    '.....kkkkkk.',
    '.....knnnnk.',
    '.....kkkknk.',
    '.....k..knk.',
    '.....k..knk.',
    '.....k..knk.',
    '..kkkk..knk.',
    '.knnnk.kknk.',
    '.knnnk.knnk.',
    '..kkk..knnk.',
    '.......kkk..',
    '............',
  ],
  cup: [
    '............',
    '...w..w.....',
    '....w..w....',
    '.kkkkkkkk...',
    '.kddddddkkk.',
    '.kwwwwwwk.k.',
    '.kwggwwwk.k.',
    '.kwwwwwwkkk.',
    '.kwwwwwwk...',
    '..kwwwwk....',
    '.kkkkkkkkk..',
    '............',
  ],
  chat: [
    '............',
    '.kkkkkkkkkk.',
    'kwwwwwwwwwwk',
    'kwkkwkkkwwwk',
    'kwwwwwwwwwwk',
    'kwkkkkwkkwwk',
    'kwwwwwwwwwwk',
    '.kkkwwkkkkk.',
    '...kwk......',
    '...kk.......',
    '............',
    '............',
  ],
  sound: [
    '............',
    '.....kk.....',
    '....kck..k..',
    '.kkkcck.k.k.',
    '.kcccck..k.k',
    '.kcccck..k.k',
    '.kcccck..k.k',
    '.kkkcck.k.k.',
    '....kck..k..',
    '.....kk.....',
    '............',
    '............',
  ],
  mute: [
    '............',
    '.....kk.....',
    '....kck.....',
    '.kkkcck.r..r',
    '.kcccck..rr.',
    '.kcccck..rr.',
    '.kcccck.r..r',
    '.kkkcck.....',
    '....kck.....',
    '.....kk.....',
    '............',
    '............',
  ],
  user: [
    '....kkkk....',
    '...kddddk...',
    '..kddddddk..',
    '..kcccccck..',
    '..kckcckck..',
    '..kcccccck..',
    '...kcrrck...',
    '....kkkk....',
    '..kkggggkk..',
    '.kggggggggk.',
    '.kggggggggk.',
    '.kkkkkkkkkk.',
  ],
  plus: [
    '............',
    '....kkkk....',
    '....kwwk....',
    '....kwwk....',
    '.kkkkwwkkkk.',
    '.kwwwwwwwwk.',
    '.kwwwwwwwwk.',
    '.kkkkwwkkkk.',
    '....kwwk....',
    '....kwwk....',
    '....kkkk....',
    '............',
  ],
  minus: [
    '............',
    '............',
    '............',
    '............',
    '.kkkkkkkkkk.',
    '.kwwwwwwwwk.',
    '.kwwwwwwwwk.',
    '.kkkkkkkkkk.',
    '............',
    '............',
    '............',
    '............',
  ],
  close: [
    '............',
    '.kk......kk.',
    '.kwk....kwk.',
    '..kwk..kwk..',
    '...kwkkwk...',
    '....kwwk....',
    '....kwwk....',
    '...kwkkwk...',
    '..kwk..kwk..',
    '.kwk....kwk.',
    '.kk......kk.',
    '............',
  ],
  rotate: [
    '............',
    '...kkkkk.k..',
    '..kyyyyykyk.',
    '.kyykkkkyyk.',
    '.kyk...kyyk.',
    '.kyk..kkkkk.',
    '.kyk........',
    '.kyk....kk..',
    '.kyykkkkyk..',
    '..kyyyyyk...',
    '...kkkkk....',
    '............',
  ],
  pickup: [
    '.....kk.....',
    '....kyyk....',
    '...kyyyyk...',
    '..kyyyyyyk..',
    '.kkkkyykkkk.',
    '....kyyk....',
    '....kyyk....',
    'kkkkkkkkkkkk',
    'kbbbbbbbbbbk',
    'kddddddddddk',
    'kbbbbbbbbbbk',
    'kkkkkkkkkkkk',
  ],
} as const;

/** Built-in icons, plus any a feature registers (see registerIcon). */
export type IconName = keyof typeof ICONS | (string & {});

const cache = new Map<string, string>();
const extraIcons = new Map<string, { rows: readonly string[]; palette: Record<string, string> }>();

/**
 * Lets a feature module add its own 12x12 icons without editing this file.
 * `palette` extends the shared one (letters not listed fall back to it).
 */
export function registerIcon(name: string, rows: readonly string[], palette: Record<string, string> = {}): void {
  extraIcons.set(name, { rows, palette });
  cache.delete(name);
}

export function iconUrl(name: IconName): string {
  let url = cache.get(name);
  if (!url) {
    const extra = extraIcons.get(name);
    const rows = extra?.rows ?? ICONS[name as keyof typeof ICONS] ?? ICONS.help;
    const colors = extra ? { ...PALETTE, ...extra.palette } : PALETTE;
    const pc = new PixelCanvas(12, 12, 1);
    rows.forEach((row, y) => {
      [...row].forEach((ch, x) => {
        const color = colors[ch];
        if (color) pc.px(x, y, color);
      });
    });
    // Hand-placed pixels: smoothed into rounded shapes rather than scaled as blocks.
    url = smoothToCanvas(pc, 8).toDataURL();
    cache.set(name, url);
  }
  return url;
}
